// A06: Files tab. Lists the connected repos, lets the Captain expand
// a tree per repo, and opens the selected file in a Monaco viewer.
// Commit-diff view lives in the DiffsTab.

import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import Editor from "@monaco-editor/react";
import {
  ApiError,
  extractRows,
  listWorkspaceRepoConnections,
  readWorkspaceFile,
} from "../api/client";
import { rowToRepoConnection } from "./rowParsers";
import type { RepoConnection } from "./types";
import { FileTreePanel } from "./FileTreePanel";
import { TabScrollbox } from "../chrome/TabScrollbox";

interface Props {
  workspaceId: number;
}

interface OpenFile {
  path: string;
  contents: string;
  binary: boolean;
  size: number;
}

function languageFor(path: string): string {
  const ext = path.toLowerCase().split(".").pop() ?? "";
  switch (ext) {
    case "ts":
    case "tsx":
      return "typescript";
    case "js":
    case "jsx":
      return "javascript";
    case "rs":
      return "rust";
    case "json":
      return "json";
    case "md":
      return "markdown";
    case "toml":
      return "toml";
    case "yml":
    case "yaml":
      return "yaml";
    case "sh":
      return "shell";
    case "css":
      return "css";
    case "html":
      return "html";
    case "py":
      return "python";
    default:
      return "plaintext";
  }
}

export function FilesTab({ workspaceId }: Props) {
  const [searchParams] = useSearchParams();
  const requestedRepo = searchParams.get("repo");
  const requestedFile = searchParams.get("file");
  const [repos, setRepos] = useState<RepoConnection[]>([]);
  const [selectedRepo, setSelectedRepo] = useState<string | null>(null);
  const [openFile, setOpenFile] = useState<OpenFile | null>(null);
  const [error, setError] = useState("");
  const lastRequestedFileRef = useRef<string | null>(null);

  useEffect(() => {
    lastRequestedFileRef.current = null;
  }, [workspaceId]);

  useEffect(() => {
    (async () => {
      try {
        const res = await listWorkspaceRepoConnections(workspaceId);
        const list = extractRows(res)
          .map(rowToRepoConnection)
          .filter((r): r is RepoConnection => r !== null);
        setRepos(list);
        if (list.length > 0 && !selectedRepo) {
          setSelectedRepo(list[0].github_repo);
        }
      } catch (err) {
        setError(
          err instanceof ApiError
            ? err.message
            : "Could not load connected repos",
        );
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceId]);

  async function onOpenFile(path: string) {
    setError("");
    try {
      const resp = await readWorkspaceFile(workspaceId, path);
      if (resp.kind === "directory") {
        return;
      }
      if (resp.kind === "binary") {
        setOpenFile({ path: resp.path, contents: "", binary: true, size: resp.size });
        return;
      }
      setOpenFile({
        path: resp.path,
        contents: resp.contents,
        binary: false,
        size: resp.contents.length,
      });
    } catch (err) {
      setError(
        err instanceof ApiError ? err.message : `Could not read ${path}`,
      );
    }
  }

  useEffect(() => {
    if (requestedRepo && repos.some((r) => r.github_repo === requestedRepo)) {
      setSelectedRepo(requestedRepo);
    }
    if (!requestedFile || requestedFile === lastRequestedFileRef.current) return;
    lastRequestedFileRef.current = requestedFile;
    void onOpenFile(requestedFile);
    // onOpenFile intentionally stays local: this effect should fire
    // only when a URL-deep-linked file changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestedFile, requestedRepo, repos, workspaceId]);

  const language = useMemo(
    () => (openFile ? languageFor(openFile.path) : "plaintext"),
    [openFile],
  );

  if (repos.length === 0) {
    return (
      <TabScrollbox>
        <div className="px-6 py-10 text-sm text-muted">
          <p className="mb-3 text-primary">No connected repos</p>
          <p>
            Attach a GitHub repo from the Workspace Settings → Connected Repos
            panel to browse its files here.
          </p>
        </div>
      </TabScrollbox>
    );
  }

  return (
    <div
      className="flex h-full flex-row gap-0 overflow-hidden"
      data-testid="files-tab"
    >
      <aside className="flex w-64 shrink-0 flex-col overflow-hidden border-r border-edge">
        <ul className="flex flex-col gap-0.5 border-b border-edge bg-surface-glass p-2 text-xs">
          {repos.map((r) => (
            <li key={r.repo_connection_id}>
              <button
                type="button"
                onClick={() => setSelectedRepo(r.github_repo)}
                className={`w-full cursor-pointer truncate rounded px-2 py-1 text-left text-[0.75rem] transition-colors ${
                  selectedRepo === r.github_repo
                    ? "bg-surface-raised text-accent"
                    : "text-muted hover:bg-surface-raised hover:text-primary"
                }`}
                data-testid={`file-tree-repo-${r.github_repo}`}
              >
                {r.github_owner}/{r.github_repo}
              </button>
            </li>
          ))}
        </ul>
        <div className="flex-1 overflow-y-auto bg-surface-glass">
          {selectedRepo && (
            <FileTreePanel
              workspaceId={workspaceId}
              repo={selectedRepo}
              onOpenFile={(p) => void onOpenFile(p)}
            />
          )}
        </div>
      </aside>
      <section className="relative flex min-w-0 flex-1 flex-col">
        {openFile ? (
          <>
            <header className="flex items-baseline justify-between border-b border-edge bg-surface-glass px-3 py-1">
              <span className="truncate font-code text-[0.6875rem] text-muted">
                {openFile.path}
              </span>
              <span className="text-[0.625rem] text-faint">
                {openFile.binary
                  ? `binary · ${openFile.size} bytes`
                  : `${language} · ${openFile.size} bytes`}
              </span>
            </header>
            <div className="flex-1 overflow-hidden">
              {openFile.binary ? (
                <p className="p-6 text-sm text-muted">
                  Binary file — preview not available.
                </p>
              ) : (
                <Editor
                  key={openFile.path}
                  height="100%"
                  defaultLanguage={language}
                  defaultValue={openFile.contents}
                  theme="vs-dark"
                  options={{
                    readOnly: true,
                    minimap: { enabled: true },
                    scrollBeyondLastLine: false,
                    fontSize: 12,
                    lineNumbers: "on",
                    wordWrap: "on",
                    automaticLayout: true,
                  }}
                />
              )}
            </div>
          </>
        ) : (
          <div className="flex flex-1 items-center justify-center p-8 text-sm text-muted">
            <p>Pick a file from the tree to view its contents.</p>
          </div>
        )}
        {error && (
          <p className="border-t border-danger bg-surface-raised px-3 py-1 text-[0.6875rem] text-danger">
            {error}
          </p>
        )}
      </section>
    </div>
  );
}
